import { useAuth } from "@clerk/clerk-react";
import {
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import {
  OrganizationNiche,
  OrganizationNicheStatus,
  TemplateIntakeForm,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface Props {
  index: number;
  form: TemplateIntakeForm;
  onChange: (e: CustomEvent, index: number) => void;
  showNichePickQuestion?: boolean;
}

const TemplateNicheQuestion = ({
  index,
  form,
  onChange,
  showNichePickQuestion,
}: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();

  const [nichesToPickFrom, setNichesToPickFrom] = useState<OrganizationNiche[]>(
    [],
  );
  const fetchNiches = async () => {
    const { niches } = await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/organization-niches`,
    );
    const activeOrPendingNiches = niches?.filter(
      (x: OrganizationNiche) =>
        x.status === OrganizationNicheStatus.active ||
        x.status === OrganizationNicheStatus.pending,
    );
    setNichesToPickFrom(activeOrPendingNiches);
  };

  useEffect(() => {
    if (currentOrg?.id && showNichePickQuestion) {
      fetchNiches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const questions = [
    {
      field: "pastBrands",
      question: form?.niche?.name
        ? `What are big brand names you've worked with in ${form?.niche?.name}?`
        : "What are the biggest brand names you have partnered with in the past?",
      required: false,
      placeholder: "Type your answer here...",
    },
    {
      field: "topPerformingPostLink",
      question: form?.niche?.name
        ? `Share a top performing post (IG or TikTok link) in ${form?.niche?.name} (preferably on a brand page):`
        : "Share a top performing post (IG or TikTok link), preferably on a brand page:",
      required: false,
      placeholder: "https://instagram.com/link",
    },
    {
      field: "partnershipReason",
      question: form?.niche?.name
        ? `Share 2-3 unique selling points about you in ${form?.niche?.name}`
        : "",
      required: true,
      placeholder: "Type your answer here...",
    },
    {
      field: "notes",
      question:
        "Is there anything specific you'd like us to include in this outreach?",
      required: false,
      placeholder: "Type your answer here...",
    },
  ];

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Template Request {index + 1}{" "}
        {form?.niche?.name ? `- ${form?.niche?.name}` : ""}
      </Typography>
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        {questions.map(
          ({ field, question, placeholder, required }, idx) =>
            question?.length > 0 && (
              <Grid item xs={12} key={idx} sx={{ mt: idx === 0 ? 0 : 2 }}>
                <FormHelperText>
                  {question}
                  {required ? " *" : ""}
                </FormHelperText>
                <TextField
                  sx={{
                    backgroundColor: "white",
                    "& .MuiInputBase-root": { backgroundColor: "white" },
                    "& .MuiFormHelperText-root": { color: "error.main" },
                  }}
                  fullWidth
                  name={field}
                  placeholder={placeholder}
                  onChange={(e) => onChange(e, index)}
                  required={required}
                  // @ts-ignore
                  value={form?.[field] || ""}
                  multiline={field === "partnershipReason"}
                  rows={field === "partnershipReason" ? 4 : 1}
                />
              </Grid>
            ),
        )}

        {nichesToPickFrom?.length > 0 && (
          <>
            <FormHelperText sx={{ mt: 2 }}>
              What niche is this template for?
            </FormHelperText>
            <Select
              sx={{
                backgroundColor: "white",
                "& .MuiInputBase-root": { backgroundColor: "white" },
                "& .MuiFormHelperText-root": { color: "error.main" },
              }}
              fullWidth
              name="nicheId"
              onChange={(e) => onChange(e, index)}
              value={form?.nicheId || ""}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Niche</em>
              </MenuItem>
              {nichesToPickFrom?.map((niche) => (
                <MenuItem value={niche?.id}>{niche.name}</MenuItem>
              ))}
            </Select>
          </>
        )}
      </Grid>
    </>
  );
};

export default TemplateNicheQuestion;
